import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import {
  BoardTasksData,
  TaskDetails,
  TaskSummary,
  CreateTaskRequest,
  UpdateTaskRequest,
  UpdateTaskScheduleRequest,
} from "../types";
import { TasksApi } from "../api";

/**
 * Query keys for tasks
 */
export const taskKeys = {
  all: ["tasks"] as const,
  lists: () => [...taskKeys.all, "list"] as const,
  list: (boardId: string) => [...taskKeys.lists(), boardId] as const,
  boardData: (boardId: string) =>
    [...taskKeys.all, "board-data", boardId] as const,
  details: () => [...taskKeys.all, "detail"] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  subTasks: (id: string) => [...taskKeys.all, "subtasks", id] as const,
};

/**
 * Hook to get all tasks and statuses for a board in one call
 */
export function useBoardTasks(
  tasksApi: TasksApi,
  boardId: string,
  options?: Omit<
    UseQueryOptions<BoardTasksData, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<BoardTasksData, Error>({
    queryKey: taskKeys.boardData(boardId),
    queryFn: () => tasksApi.getBoardTasksWithStatuses(boardId),
    enabled: !!boardId,
    ...options,
  });
}

/**
 * Hook to get all tasks for a board
 */
export function useTasks(
  tasksApi: TasksApi,
  boardId: string,
  options?: Omit<UseQueryOptions<TaskSummary[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery<TaskSummary[], Error>({
    queryKey: taskKeys.list(boardId),
    queryFn: () => tasksApi.getTasks(boardId),
    enabled: !!boardId,
    ...options,
  });
}

/**
 * Hook to get a specific task
 */
export function useTask(
  tasksApi: TasksApi,
  taskId: string,
  boardId: string,
  options?: Omit<UseQueryOptions<TaskDetails, Error>, "queryKey" | "queryFn">,
) {
  return useQuery<TaskDetails, Error>({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => tasksApi.getTaskDetails(boardId, taskId),
    enabled: !!taskId,
    ...options,
  });
}

/**
 * Hook to create a task
 */
export function useCreateTask(
  tasksApi: TasksApi,
  options?: UseMutationOptions<TaskSummary, Error, CreateTaskRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation<TaskSummary, Error, CreateTaskRequest>({
    mutationFn: (request) => tasksApi.createTask(request),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate tasks list for the board
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      if (variables.boardId) {
        queryClient.invalidateQueries({
          queryKey: taskKeys.boardData(variables.boardId),
        });
      }
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to update a task
 */
export function useUpdateTask(
  tasksApi: TasksApi,
  options?: UseMutationOptions<
    TaskSummary,
    Error,
    { boardId: string; taskId: string; request: UpdateTaskRequest }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    TaskSummary,
    Error,
    { boardId: string; taskId: string; request: UpdateTaskRequest }
  >({
    mutationFn: ({ boardId, taskId, request }) =>
      tasksApi.updateTask(boardId, taskId, request),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate tasks list and specific task
      queryClient.invalidateQueries({
        queryKey: taskKeys.list(variables.boardId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.boardData(variables.boardId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to update only a task's schedule (day, time of day, duration).
 *
 * Separate from {@link useUpdateTask} because the schedule view works from task
 * summaries, which carry no notes or sub-tasks to send back.
 */
export function useUpdateTaskSchedule(
  tasksApi: TasksApi,
  options?: UseMutationOptions<
    TaskSummary,
    Error,
    {
      boardId: string;
      taskId: string;
      request: UpdateTaskScheduleRequest;
      hubConnectionId?: string;
    }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    TaskSummary,
    Error,
    {
      boardId: string;
      taskId: string;
      request: UpdateTaskScheduleRequest;
      hubConnectionId?: string;
    }
  >({
    mutationFn: ({ boardId, taskId, request, hubConnectionId }) =>
      tasksApi.updateTaskSchedule(boardId, taskId, request, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.list(variables.boardId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.boardData(variables.boardId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to delete a task
 */
export function useDeleteTask(
  tasksApi: TasksApi,
  options?: UseMutationOptions<
    void,
    Error,
    { taskId: string; boardId: string }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { taskId: string; boardId: string }>({
    mutationFn: ({ taskId, boardId }) => tasksApi.deleteTask(boardId, taskId),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate tasks list and remove specific task from cache
      queryClient.invalidateQueries({
        queryKey: taskKeys.list(variables.boardId),
      });
      queryClient.removeQueries({
        queryKey: taskKeys.detail(variables.taskId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.boardData(variables.boardId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to move a task to a different status
 */
export function useMoveTask(
  tasksApi: TasksApi,
  options?: UseMutationOptions<
    TaskSummary,
    Error,
    { boardId: string; taskId: string; statusId: string; order?: number }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    TaskSummary,
    Error,
    { boardId: string; taskId: string; statusId: string; order?: number }
  >({
    mutationFn: ({ boardId, taskId, statusId, order }) =>
      tasksApi.moveTask(boardId, taskId, statusId, order),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.list(variables.boardId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.boardData(variables.boardId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}
